import {
  SCAN_PHOTO_TIMELINE_DAY,
  SCAN_PHOTO_TIMELINE_MINUTE,
  SCAN_PHOTO_TIMELINE_MONTH,
  SCAN_PHOTO_TIMELINE_WEEK,
  type ScanPhotoTimelinePeriod,
} from '@kaiizen/kai-scan-template-system';

import { inDebugMode } from '@/Client/Lib';

export type ProgressAltPeriodId = 'day' | 'week' | 'month' | 'minute';

/**
 * How far apart two photos can be and still share a row, and what to call each choice.
 *
 * Three names per period because the picker is offered three widths. `label` is the whole word, read
 * aloud and never abbreviated. `shortLabel` is what the open date column's header cell has room for,
 * the whole set having to fit that column. `initialLabel` is what survives the column collapsing: one
 * character, because at that width one character is what there is.
 */
export type ProgressAltPeriodOption = {
  id: ProgressAltPeriodId;
  label: string;
  shortLabel: string;
  initialLabel: string;
  period: ScanPhotoTimelinePeriod;
};

/** What a renovation is actually measured in. */
const STANDARD_PERIOD_OPTIONS: readonly ProgressAltPeriodOption[] = [
  { id: 'day', label: 'Day', shortLabel: 'Day', initialLabel: 'D', period: SCAN_PHOTO_TIMELINE_DAY },
  { id: 'week', label: 'Week', shortLabel: 'Week', initialLabel: 'W', period: SCAN_PHOTO_TIMELINE_WEEK },
  { id: 'month', label: 'Month', shortLabel: 'Month', initialLabel: 'M', period: SCAN_PHOTO_TIMELINE_MONTH },
];

/**
 * A fourth grouping, hidden behind `?debug`, working exactly as the three above it do.
 *
 * A dev session cannot live through a week of renovation progress. Minute-wide buckets are what let
 * it add a photo, wait a moment, add another, and watch the two land in rows of their own — which is
 * the whole of what this is for, and why one bucket size is enough of them.
 *
 * Its `initialLabel` is the lowercase of Month's on purpose: the collapsed column has room for one
 * character and both words open with the same letter, so the case is what tells them apart.
 */
const MINUTE_PERIOD_OPTION: ProgressAltPeriodOption = {
  id: 'minute',
  label: 'Minute',
  shortLabel: 'Min',
  initialLabel: 'm',
  period: SCAN_PHOTO_TIMELINE_MINUTE,
};

const ALL_PERIOD_OPTIONS: readonly ProgressAltPeriodOption[] = [
  ...STANDARD_PERIOD_OPTIONS,
  MINUTE_PERIOD_OPTION,
];

/** Where the picker opens when the URL names no grouping of its own — the debug set included. */
export const DEFAULT_PROGRESS_ALT_PERIOD_ID: ProgressAltPeriodId = 'day';

/**
 * Which periods to offer. Debug mode — the same switch `AdminScanPage` already reads for its own
 * developer affordances — adds the minute to the three a renovation is discussed in rather than
 * swapping them out, so a dev session reads the same grid and the same default as production does.
 */
export const getProgressAltPeriodOptions = (): readonly ProgressAltPeriodOption[] => inDebugMode() ? ALL_PERIOD_OPTIONS : STANDARD_PERIOD_OPTIONS;

/** Resolves across every period, so one named by the URL survives the flag being switched off. */
export const getProgressAltPeriod = (periodId: ProgressAltPeriodId): ScanPhotoTimelinePeriod =>
  ALL_PERIOD_OPTIONS.find(option => option.id === periodId)?.period ?? SCAN_PHOTO_TIMELINE_DAY;

/**
 * Whether a string off the URL names a grouping. Every period answers, the hidden one included: a
 * link naming the minute is honored without `?debug`, which only governs what the picker shows.
 */
export const isProgressAltPeriodId = (value: string): value is ProgressAltPeriodId => ALL_PERIOD_OPTIONS.some(option => option.id === value);
